import { useEffect, useState } from "react";
import {
  githubStatus,
  githubConnect,
  githubConnectStatus,
  githubDisconnect,
  githubRepos,
  githubAddRepo,
  checkPush,
} from "../api.js";
import { useSession } from "../data/usePage.js";
import { invalidate } from "../data/useFetch.js";
import type {
  GithubStatus,
  GithubRepo,
  DeviceFlowStart,
  PushCheckResult,
} from "../../shared/types.js";

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/**
 * The GitHub push-credential panel: shows whether the shared GitHub App is
 * configured/connected and drives the device flow (connect / disconnect). It is
 * the "extra UI" for the `github` config file (see config/configTypes), and is
 * also used by the legacy Settings page. Reads `github.clientId` indirectly via
 * the server's /github/status (configured = a client id is set).
 */
export function GithubPanel() {
  const [status, setStatus] = useState<GithubStatus | null>(null);
  const [flow, setFlow] = useState<DeviceFlowStart | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [push, setPush] = useState<PushCheckResult | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [repos, setRepos] = useState<GithubRepo[] | null>(null);
  const [reposBusy, setReposBusy] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const session = useSession();
  // Repos already served — show "added" instead of an Add button.
  const served =
    session.status === "ready"
      ? new Set(session.data.repos.map((r) => r.name))
      : new Set<string>();

  const reload = (): Promise<void> =>
    githubStatus()
      .then(setStatus)
      .catch((e: unknown) => setErr(errMsg(e)));

  useEffect(() => {
    void reload();
  }, []);

  // Poll the device flow once started, every `interval` seconds until the admin
  // authorizes (or the code expires).
  useEffect(() => {
    if (!flow) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const deadline = Date.now() + flow.expiresIn * 1000;
    const tick = async (): Promise<void> => {
      if (stop) return;
      if (Date.now() > deadline) {
        setErr("Code expired — try Connect again.");
        setFlow(null);
        return;
      }
      try {
        const r = await githubConnectStatus(flow.deviceCode);
        if (stop) return;
        if (r.status === "connected") {
          setFlow(null);
          await reload();
          return;
        }
        if (r.status === "error") {
          setErr(r.error);
          setFlow(null);
          return;
        }
      } catch (e) {
        if (stop) return;
        setErr(errMsg(e));
        setFlow(null);
        return;
      }
      timer = setTimeout(() => void tick(), flow.interval * 1000);
    };
    timer = setTimeout(() => void tick(), flow.interval * 1000);
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [flow]);

  const connect = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    setCopied(false);
    try {
      setFlow(await githubConnect());
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await githubDisconnect();
      await reload();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  // Probe whether a push would actually succeed with the current connection.
  const runPush = async (): Promise<void> => {
    setPushBusy(true);
    setPush(null);
    try {
      setPush(await checkPush());
    } catch (e) {
      setPush({ ok: false, error: errMsg(e) });
    } finally {
      setPushBusy(false);
    }
  };

  // List the repos the connected account can see (to pick one to serve).
  const listRepos = async (): Promise<void> => {
    setReposBusy(true);
    setErr(null);
    try {
      setRepos(await githubRepos());
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setReposBusy(false);
    }
  };

  // Add a repo to the served set. The server checks access first, writes the
  // datawiki-config registry, and the orchestrator clones it; invalidating
  // `session` flips the row to "added" and refreshes the sidebar repo list.
  const addRepo = async (r: GithubRepo): Promise<void> => {
    setAdding(r.fullName);
    setErr(null);
    try {
      await githubAddRepo({ fullName: r.fullName, cloneUrl: r.cloneUrl, branch: r.defaultBranch });
      invalidate("session");
      invalidate("tree");
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setAdding(null);
    }
  };

  if (!status) return <p className="dw-muted">Loading GitHub status…</p>;

  return (
    <div className="dw-settings-check">
      {!status.configured ? (
        <p className="dw-settings-off">
          No GitHub client ID — edits commit locally only. Set{" "}
          <code>clientId</code> above to enable pushing.
        </p>
      ) : status.connected ? (
        <p className="dw-settings-ok">
          ✓ Connected as <strong>@{status.login ?? "?"}</strong>{" "}
          <button type="button" onClick={() => void disconnect()} disabled={busy}>
            Disconnect
          </button>
        </p>
      ) : flow ? (
        <div>
          <p>
            Go to{" "}
            <a href={flow.verificationUri} target="_blank" rel="noreferrer">
              {flow.verificationUri}
            </a>{" "}
            and enter this code:
          </p>
          <p className="dw-github-usercode dw-field-mono">
            <strong>{flow.userCode}</strong>{" "}
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(flow.userCode);
                setCopied(true);
              }}
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </p>
          <p className="dw-muted">Waiting for authorization…</p>
        </div>
      ) : (
        <p className="dw-settings-off">
          Not connected.{" "}
          <button
            type="button"
            className="primary"
            onClick={() => void connect()}
            disabled={busy}
          >
            {busy ? "Starting…" : "Connect GitHub"}
          </button>
        </p>
      )}
      {status.configured && (
        <p>
          <button type="button" onClick={() => void runPush()} disabled={pushBusy}>
            {pushBusy ? "Checking…" : "Check push"}
          </button>
        </p>
      )}
      {push && <PushResult result={push} />}
      {status.connected && (
        <div className="dw-gh-repos">
          <p>
            <button type="button" onClick={() => void listRepos()} disabled={reposBusy}>
              {reposBusy ? "Loading…" : "List repos"}
            </button>
          </p>
          {repos && repos.length === 0 && (
            <p className="dw-muted">No repos visible to this account.</p>
          )}
          {repos && repos.length > 0 && (
            <ul className="dw-gh-repo-list">
              {repos.map((r) => (
                <li key={r.fullName}>
                  {served.has(r.name) ? (
                    <span className="dw-settings-ok">added ✓</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void addRepo(r)}
                      disabled={adding !== null}
                    >
                      {adding === r.fullName ? "Adding…" : "Add"}
                    </button>
                  )}{" "}
                  <code>{r.fullName}</code>{" "}
                  <span className="dw-muted">
                    {r.private ? "private" : "public"} · {r.defaultBranch}
                    {r.canPush ? "" : " · read-only"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {err && <pre className="dw-error">{err}</pre>}
    </div>
  );
}

function PushResult({ result }: { result: PushCheckResult }) {
  if (result.ok) {
    return <p className="dw-settings-ok">✓ Push OK — {result.repo}</p>;
  }
  return (
    <>
      <p className="dw-settings-fail">
        ✗ {result.canPush === false ? "No push permission" : "Push check failed"}
        {result.repo ? ` — ${result.repo}` : ""}
      </p>
      {result.error && <pre className="dw-error">{result.error}</pre>}
    </>
  );
}
